/**
 * Cadastro de conteúdo: a fonte, a classificação, e a conferência antes de gravar.
 *
 * **Cinco fontes, não seis.** O sistema antigo tem também "arquivo", com upload
 * de PDF ou imagem — mas nenhum endpoint de conteúdo da ApiSistema aceita
 * binário: o campo de arquivos é uma lista de *nomes*. Gravar um nome sem subir
 * o arquivo criaria um item apontando para nada, e o erro só apareceria depois,
 * para o cliente, como conteúdo quebrado. Essa fonte continua no sistema antigo.
 *
 * **O espanhol é dito em voz alta.** Metade do acervo é espanhol — 9.711 em
 * português e 8.878 em espanhol. O sistema antigo replica sem avisar; aqui a
 * réplica é uma caixa marcada por padrão, então o gesto não muda mas fica claro
 * que se está criando dois registros.
 */

import { useMemo, useState } from "react"
import { usarIncluirConteudos, FONTES, type Fonte, type NovoConteudo } from "@/api/conteudo"
import { filtrarPelaMarca, usarCatalogosDeConteudo } from "@/api/catalogosDeConteudo"
import { Botao, Seletor, mensagem, type Concluir } from "./Conteudo"
import { combinacoes, LIMITE_DO_LOTE, MultiSelecao } from "./MultiSelecao"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { alturaDaJanela } from "@/kit/flutuante"

export function IncluirConteudo({ aoFechar, aoAvisar, aoConcluir, idiomaAtual }: {
  aoFechar: () => void
  aoAvisar: (m: string | null) => void
  aoConcluir: Concluir
  idiomaAtual: number
}) {
  const catalogos = usarCatalogosDeConteudo()
  const incluir = usarIncluirConteudos()

  const [fonte, setFonte] = useState<Fonte>("link")
  const [link, setLink] = useState("")
  const [projetoId, setProjetoId] = useState(0)
  const [diagramaId, setDiagramaId] = useState(0)
  const [componenteId, setComponenteId] = useState(0)
  const [modeloId, setModeloId] = useState(0)

  const [descricao, setDescricao] = useState("")
  const [produtoId, setProdutoId] = useState(-1)
  const [origemId, setOrigemId] = useState(0)
  const [marcaId, setMarcaId] = useState(0)
  const [anoIni, setAnoIni] = useState(0)
  const [anoFim, setAnoFim] = useState(0)
  const [replicarEspanhol, setReplicarEspanhol] = useState(true)

  const [veiculos, setVeiculos] = useState<number[]>([])
  const [motores, setMotores] = useState<number[]>([])
  const [assuntos, setAssuntos] = useState<number[]>([])
  const [sistemas, setSistemas] = useState<number[]>([])
  const [nomes, setNomes] = useState<number[]>([])
  const [fabricantes, setFabricantes] = useState<number[]>([])

  const combos = useMemo(
    () => combinacoes({ veiculos, motores, assuntos, sistemas, nomes, fabricantes }),
    [veiculos, motores, assuntos, sistemas, nomes, fabricantes])

  const esquema = FONTES.find((f) => f.chave === fonte)!
  const fonteCompleta =
    fonte === "diagramaSVG" ? projetoId > 0 && diagramaId > 0
    : fonte === "pinout_grafico" ? projetoId > 0 && componenteId > 0 && modeloId > 0
    : link.trim().length > 0

  const problemas: string[] = []
  if (!fonteCompleta) problemas.push(`Preencha a fonte (${esquema.rotulo}).`)
  if (produtoId < 0) problemas.push("Escolha o produto.")
  if (origemId <= 0) problemas.push("Escolha a origem.")
  if (!descricao.trim()) problemas.push("Escreva a descrição.")
  if (combos.length === 0) problemas.push("Classifique em ao menos um campo.")
  if (combos.length > LIMITE_DO_LOTE)
    problemas.push(`São ${combos.length} combinações e o lote aceita ${LIMITE_DO_LOTE}. Grave em partes.`)

  function gravar() {
    aoAvisar(null)
    const itens: NovoConteudo[] = combos.map((c) => ({
      tipo: fonte,
      produtoId, origemId, baseId: idiomaAtual,
      veiculoId: c.veiculo || undefined,
      motorId: c.motor || undefined,
      assuntoId: c.assunto || undefined,
      sistemaId: c.sistema || undefined,
      nomeSistemaId: c.nome || undefined,
      fabSistemaId: c.fabricante || undefined,
      anoInicial: anoIni || undefined,
      anoFinal: anoFim || undefined,
      descricao: descricao.trim(),
      projetoId: projetoId || undefined,
      diagramaId: diagramaId || undefined,
      componenteId: componenteId || undefined,
      modeloId: modeloId || undefined,
      link: link.trim() || undefined,
      replicarEspanhol,
    }))

    incluir.mutate(itens, {
      onSuccess: (r) => {
        const criados = (Array.isArray(r) ? r : [r]).map((x) => x?.dadoId).filter((id): id is number => !!id)
        const n = criados.length || 1
        aoConcluir(`${n} ${n === 1 ? "conteúdo criado" : "conteúdos criados"}${
          replicarEspanhol ? ", com a réplica em espanhol" : ""}${
          n === 1 && criados[0] ? ` — id ${criados[0]}` : ""}.`, criados)
        aoFechar()
      },
      onError: (e) => aoAvisar(mensagem(e)),
    })
  }

  return (
    <div
      role="dialog"
      aria-label="Incluir conteúdo"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 40,
        display: "flex", justifyContent: "center", alignItems: "flex-start",
        padding: `${alturaDaJanela(4)} 16px`,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !incluir.isPending) aoFechar() }}
    >
      <section
        onKeyDown={(e) => { if (e.key === "Escape" && !incluir.isPending) aoFechar() }}
        style={{
          background: "var(--surface)", borderRadius: 8, width: "min(720px, 100%)",
          // Dividido pela escala (A−/A+): o modal mora no <main> com zoom, e "92vh" em 130%
          // passava da janela — o fim do formulário, com o Gravar, ficava fora de alcance.
          maxHeight: alturaDaJanela(92), overflowY: "auto", overscrollBehavior: "contain",
          boxShadow: "0 16px 48px rgba(0,0,0,.35)",
        }}
      >
        <div style={{
          position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
          display: "flex", alignItems: "center", padding: "12px 16px",
          borderBottom: "1px solid var(--line2)",
        }}>
          <b style={{ fontSize: 14 }}>Incluir conteúdo</b>
          <button
            onClick={aoFechar}
            aria-label="Fechar"
            style={{
              marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
              color: "var(--txt3)", fontSize: 18, lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <AvisoDeDadoGlobal oQueMuda="O conteúdo vale para todos os projetos e é o que o cliente vê no site" />

        <div style={{ padding: 16 }}>
          <h4 style={titulo}>1 · De onde vem a informação</h4>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
            {FONTES.map((f) => (
              <button
                key={f.chave}
                onClick={() => setFonte(f.chave)}
                style={{
                  border: "1px solid " + (fonte === f.chave ? "var(--acao)" : "var(--line)"),
                  borderRadius: 5, padding: "4px 10px", fontSize: 11.5, cursor: "pointer",
                  background: fonte === f.chave ? "var(--acao-soft)" : "var(--surface)",
                  color: fonte === f.chave ? "var(--acao-on-soft)" : "var(--txt)",
                }}
              >
                {f.rotulo}
              </button>
            ))}
          </div>

          <p style={{ margin: "0 0 10px", fontSize: 11, color: "var(--txt3)", lineHeight: 1.5 }}>
            O <b>arquivo</b> (PDF, imagem) é anexado depois, na ficha do conteúdo criado.
          </p>

          {fonte === "diagramaSVG" && (
            <div style={{ display: "flex", gap: 8 }}>
              <Numero rotulo="Projeto" valor={projetoId} aoMudar={setProjetoId} />
              <Numero rotulo="Diagrama" valor={diagramaId} aoMudar={setDiagramaId} />
            </div>
          )}
          {fonte === "pinout_grafico" && (
            <div style={{ display: "flex", gap: 8 }}>
              <Numero rotulo="Projeto" valor={projetoId} aoMudar={setProjetoId} />
              <Numero rotulo="Componente" valor={componenteId} aoMudar={setComponenteId} />
              <Numero rotulo="Modelo" valor={modeloId} aoMudar={setModeloId} />
            </div>
          )}
          {(fonte === "youtube" || fonte === "vimeo" || fonte === "link") && (
            <>
              <label style={rot}>Endereço</label>
              <input value={link} onChange={(e) => setLink(e.target.value)}
                     placeholder="https://…" style={campo} />
            </>
          )}

          <h4 style={{ ...titulo, marginTop: 18 }}>2 · Como ele é classificado</h4>

          <label style={rot}>Descrição</label>
          <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={2}
                    style={{ ...campo, resize: "vertical", fontFamily: "inherit" }} />

          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Seletor rotulo="Produto" itens={catalogos.produtos.itens}
                       valor={produtoId} aoMudar={setProdutoId} />
            </div>
            <div style={{ flex: 1 }}>
              <Seletor rotulo="Origem" itens={catalogos.origens.itens}
                       valor={origemId} aoMudar={setOrigemId} />
            </div>
          </div>

          <Seletor rotulo="Marca (filtra veículo e motor)" itens={catalogos.marcas.itens}
                   valor={marcaId} aoMudar={(v) => { setMarcaId(v); setVeiculos([]); setMotores([]) }} />

          <MultiSelecao rotulo="Veículos" itens={filtrarPelaMarca(catalogos.veiculos.itens, marcaId)}
                        escolhidos={veiculos} aoMudar={setVeiculos} />
          <MultiSelecao rotulo="Motores" itens={filtrarPelaMarca(catalogos.motores.itens, marcaId)}
                        escolhidos={motores} aoMudar={setMotores} />
          <MultiSelecao rotulo="Assuntos" itens={catalogos.assuntos.itens}
                        escolhidos={assuntos} aoMudar={setAssuntos} />
          <MultiSelecao rotulo="Sistemas" itens={catalogos.sistemas.itens}
                        escolhidos={sistemas} aoMudar={setSistemas} />
          <MultiSelecao rotulo="Nomes de sistema" itens={catalogos.nomes.itens}
                        escolhidos={nomes} aoMudar={setNomes} />
          <MultiSelecao rotulo="Fabricantes" itens={catalogos.fabricantes.itens}
                        escolhidos={fabricantes} aoMudar={setFabricantes} />

          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={rot}>Ano inicial</label>
              <input type="number" value={anoIni || ""} onChange={(e) => setAnoIni(Number(e.target.value))}
                     style={campo} placeholder="—" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={rot}>Ano final</label>
              <input type="number" value={anoFim || ""} onChange={(e) => setAnoFim(Number(e.target.value))}
                     style={campo} placeholder="—" />
            </div>
          </div>

          <label style={{
            display: "flex", alignItems: "flex-start", gap: 7, marginTop: 12,
            fontSize: 11.5, lineHeight: 1.5, cursor: "pointer",
          }}>
            <input type="checkbox" checked={replicarEspanhol}
                   onChange={(e) => setReplicarEspanhol(e.target.checked)} style={{ marginTop: 2 }} />
            <span>
              <b>Criar também a versão em espanhol.</b> Metade do acervo é espanhol; o sistema
              antigo faz isso sempre, sem perguntar. Marcado, cada combinação vira dois registros.
            </span>
          </label>

          <h4 style={{ ...titulo, marginTop: 18 }}>3 · Conferência</h4>

          {combos.length > 0 && (
            <p style={{
              margin: "0 0 8px", padding: "8px 10px", borderRadius: 6,
              background: "var(--surface2)", fontSize: 12, lineHeight: 1.55,
            }}>
              <b>{combos.length} {combos.length === 1 ? "conteúdo" : "conteúdos"}</b> a criar
              {replicarEspanhol && <> — {combos.length * 2} registros, com o espanhol</>}.
            </p>
          )}

          {problemas.length > 0 && (
            <ul style={{
              margin: "0 0 8px", paddingLeft: 18, fontSize: 11.5,
              color: "var(--txt2)", lineHeight: 1.6,
            }}>
              {problemas.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <Botao aoClicar={gravar} destaque
                   desligado={problemas.length > 0} ocupado={incluir.isPending}>
              {incluir.isPending ? "Gravando…" : "Gravar"}
            </Botao>
            <Botao aoClicar={aoFechar} desligado={incluir.isPending}>Cancelar</Botao>
          </div>
        </div>
      </section>
    </div>
  )
}

function Numero({ rotulo, valor, aoMudar }: {
  rotulo: string
  valor: number
  aoMudar: (v: number) => void
}) {
  return (
    <div style={{ flex: 1 }}>
      <label style={rot}>{rotulo}</label>
      <input type="number" value={valor || ""} onChange={(e) => aoMudar(Number(e.target.value))}
             style={campo} placeholder="—" />
    </div>
  )
}

const titulo: React.CSSProperties = {
  margin: "0 0 8px", fontSize: 11, fontWeight: 600, letterSpacing: ".04em",
  textTransform: "uppercase", color: "var(--txt3)",
}
const rot: React.CSSProperties = {
  display: "block", fontSize: 10.5, color: "var(--txt2)", margin: "9px 0 3px",
}
const campo: React.CSSProperties = {
  width: "100%", padding: "5px 7px", fontSize: 12,
  border: "1px solid var(--line)", borderRadius: 5,
  background: "var(--surface)", color: "var(--txt)",
}
